import "./App.css";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import AuthLayout from "./common/components/AuthLayout";
import LoginContainer from "./auth/containers/LoginContainer";
import MainLayout from "./common/components/MainLayout";
import GoogleCallback from "./auth/containers/GoogleCallback";
import SignupContainer from "./auth/containers/SignupContainer";
import { PopupProvider } from "./common/contexts/PopupContext";
import { QueryClient, QueryClientProvider } from "react-query";
import VerifySignupContainer from "./auth/containers/VerifySignupContainer";
import VerifySubscriber from "./auth/containers/VerifySubscriberContainer";
import AuthProvider, { useAuth } from "./common/contexts/AuthContext";
import { checkAuth } from "./common/helpers/common";
import ChangePasswordContainer from "./auth/containers/ChangePasswordContainer";
import ForgotPasswordContainer from "./auth/containers/ForgotPasswordContainer";
import ChooseOrganizationContainer from "./organization/containers/ChooseOrganizationContainer";
import ProfileContainer from "./profile/containers/ProfileContainer";
import ResetPasswordContainer from "./resetPassword/containers/ResetPasswordContainer";
import UsersContainer from "./users/containers/UsersContainer";
import PagesContainer from "./pages/containers/PagesContainer";
import AppProvider from "./common/contexts/AppContext";
import CheckInvitationContainer from "./auth/containers/CheckInvitationContainer";
import ListComponentsContainer from "./components/containers/ListComponentsContainer";
import ComponentFormContainer from "./components/containers/ComponentFormContainer";
import IncidentContainer from "./components/containers/IncidentContainer";
import ListIncidentsContainer from "./components/containers/ListIncidentsContainer";
import ListIncidents from "./components/containers/ListIncidents";
import Incident from "./components/containers/Incident";
import IncidentUpdate from "./components/containers/IncidentUpdate";
import StatusApp from "./statusApp/Dashboard";
import MaintainanceForm from "./components/components/MaintainanceForm";
import MaintainanceContainer from "./components/containers/MaintenanceContainer";
import SubscriberContainer from "./subscribers/containers/SubscriberContainer";
import StatusEmbed from "./embed/containers/StatusEmbed";

const queryClient = new QueryClient();

function Dashboard() {
  return "Dashboard";
}

function NotFound() {
  return <Navigate to={checkAuth() ? "dashboard" : "login"} />;
}

const publicRoutes = [
  {
    path: "/",
    element: <AuthLayout />,
    children: [
      {
        path: "login",
        element: <LoginContainer />,
      },
      {
        path: "signup",
        element: <SignupContainer />,
      },
      {
        path: "signup/verify/:token",
        element: <VerifySignupContainer />,
      },
      {
        path: "forgot-password",
        element: <ForgotPasswordContainer />,
      },
      {
        path: "change-password/:token",
        element: <ChangePasswordContainer />,
      },
      {
        path: "google-callback",
        element: <GoogleCallback />,
      },
    ],
  },
  {
    path: "page/:pageId/subscriber/:token",
    element: <VerifySubscriber />,
  },
];

const privateRoutes = [
  {
    path: "/",
    element: <MainLayout />,
    children: [
      {
        path: "dashboard",
        element: <Dashboard />,
      },
      {
        path: "profile",
        element: <ProfileContainer />,
      },
      {
        path: "reset-password",
        element: <ResetPasswordContainer />,
      },
      {
        path: "users",
        element: <UsersContainer />,
      },
      {
        path: "pages",
        element: <PagesContainer />,
      },
      {
        path: "components",
        element: <ListComponentsContainer />,
      },
      {
        path: "component-form",
        element: <ComponentFormContainer />,
      },
      {
        path: "component-form/:id",
        element: <ComponentFormContainer />,
      },
      {
        path: "incidents",
        element: <ListIncidentsContainer />,
      },
      {
        path: "add_incident",
        element: <IncidentContainer />,
      },
      {
        path: "view_incidents",
        element: <ListIncidents />,
      },
      {
        path: "createIncident",
        element: <Incident />,
      },
      {
        path: "incident/:id",
        element: <IncidentUpdate />,
      },
      {
        path: "createMaintainance",
        element: <MaintainanceForm />,
      },
      {
        path: "maintenance/:id",
        element: <MaintainanceContainer />,
      },
      {
        path: "subscribers",
        element: <SubscriberContainer />,
      },
      {
        path: "statusEmbed",
        element: <StatusEmbed />,
      },
    ],
  },
];

const commonRoutes = [
  {
    path: "organization",
    element: <ChooseOrganizationContainer />,
  },
  {
    path: "check-invitation/:token",
    element: <CheckInvitationContainer />,
  },
  {
    path: "status/:pageId",
    element: <Dashboard />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
  {
    path: "status-page/:pageId",
    element: <StatusApp />,
  },
];

function App() {
  return (
    <div>
      <QueryClientProvider client={queryClient}>
        <Router />
      </QueryClientProvider>
    </div>
  );
}

export default App;

function Router() {
  return (
    <PopupProvider>
      <AppProvider>
        <AuthProvider>
          <ProvideRouter />
        </AuthProvider>
      </AppProvider>
    </PopupProvider>
  );
}

function ProvideRouter() {
  const { accessToken } = useAuth();

  const routes = accessToken ? privateRoutes : publicRoutes;

  const router = createBrowserRouter([...routes, ...commonRoutes]);

  return <RouterProvider router={router} />;
}
